import React, { useEffect, useState } from 'react';
import {
  BooleanField,
  BooleanInput,
  Button,
  Create,
  Datagrid,
  DateTimeInput,
  Edit,
  EditButton,
  Filter,
  List,
  NumberInput,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';
import { useToasts } from 'react-toast-notifications';
import { APIService } from '../../common/api.service';

const AvailableFields = () => {
  const [fields, setFields] = useState<string[] | null>(null);

  useEffect(() => {
    APIService.getAutomaticExportFields().then((data) => {
      setFields(data);
    });
  }, []);

  return (
    <div style={{ marginTop: '10px' }}>
      <strong>Available fields:</strong> <pre>{fields ? JSON.stringify(fields, null, 2) : 'Loading...'}</pre>
    </div>
  );
};

const AutomaticExportFilter = (props: any) => (
  <Filter {...props}>
    <NumberInput label="Race ID" source="roomId||eq" alwaysOn />
    <TextInput label="Race Title" source="room.event_title" alwaysOn />
    <TextInput label="Email" source="email" alwaysOn />
  </Filter>
);

export const AutomaticExportList = (props: any) => (
  <List {...props} filters={<AutomaticExportFilter />}>
    <Datagrid rowClick="show">
      <TextField source="id" />
      <TextField label="Race ID" source="roomId" />
      <TextField source="email" />
      <TextField source="schedule" />
      <TextField source="weekDay" />
      <TextField source="exportHourUtc" />
      <TextField source="exportMode" />
      <BooleanField source="isActive" label="Active?" />
      <EditButton />
    </Datagrid>
  </List>
);

const CommonFormPart = (props: any) => (
  <span>
    <TextInput source="email" fullWidth />
    <NumberInput source="roomId" label="Race ID" fullWidth />
    <SelectInput source="schedule" choices={['daily', 'weekly'].map((value) => ({ id: value, name: value }))} fullWidth />
    <SelectInput
      source="weekDay"
      label="Weekday"
      choices={['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((value, idx) => ({
        id: idx,
        name: value,
      }))}
      fullWidth
    />
    <NumberInput source="exportHourUtc" label="Hour (0-23)" fullWidth />
    <SelectInput
      source="exportMode"
      label="Export Mode"
      choices={['full', 'incremental'].map((value) => ({ id: value, name: value }))}
      fullWidth
    />
    <BooleanInput source="isActive" label="Active?" fullWidth />
    <DateTimeInput source="nextExportAt" label="Next Export" fullWidth />
    <TextInput
      source="exportFields"
      helperText='Leave empty for default values. Example: ["participation.id", "participation.time", "user.email"]'
      fullWidth
      format={(v: any) => (v === undefined || v === null ? '' : typeof v === 'string' ? v : JSON.stringify(v))}
      parse={(v: string) => v}
    />
    <AvailableFields />
  </span>
);

const transformExportFields = (data: any) => {
  const raw = data.exportFields;
  if (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '')) {
    return { ...data, exportFields: null };
  }
  if (typeof raw !== 'string') {
    return data;
  }
  try {
    return { ...data, exportFields: JSON.parse(raw) };
  } catch (e) {
    throw new Error('exportFields must be valid JSON (e.g. ["participation.id", "user.email"])');
  }
};

export const AutomaticExportCreate = (props: any) => (
  <Create {...props} transform={transformExportFields}>
    <SimpleForm redirect="show">
      <CommonFormPart {...props} />
    </SimpleForm>
  </Create>
);

const AutomaticExportEditActions = (props: any) => (
  <TopToolbar>
    <ShowButton variant="contained" basePath={props.basePath} record={props.data} style={{ marginRight: '5px' }} />
  </TopToolbar>
);

export const AutomaticExportEdit = (props: any) => (
  <Edit {...props} actions={<AutomaticExportEditActions />} undoable={false} transform={transformExportFields}>
    <SimpleForm redirect="edit">
      <CommonFormPart {...props} />
    </SimpleForm>
  </Edit>
);

const TriggerExportButton = ({ record }: any) => {
  const { addToast } = useToasts();
  const triggerExport = () => {
    APIService.triggerAutomaticExport(record.id)
      .then(() => {
        addToast('Export triggered', { appearance: 'success', autoDismiss: true });
      })
      .catch(() => {
        addToast('Failed to trigger export', { appearance: 'error', autoDismiss: true });
      });
  };
  return <Button variant="contained" color="primary" label="Trigger Export" onClick={triggerExport} />;
};

const AutomaticExportShowActions = (props: any) => (
  <TopToolbar>
    <EditButton basePath={props.basePath} record={props.data} style={{ marginRight: '5px' }} />
    {props.data && <TriggerExportButton record={props.data} />}
  </TopToolbar>
);

export const AutomaticExportShow = (props: any) => (
  <Show {...props} actions={<AutomaticExportShowActions />}>
    <TabbedShowLayout>
      <Tab label="General">
        <TextField source="id" />
        <TextField label="Race ID" source="roomId" />
        <TextField source="email" />
        <TextField source="schedule" />
        <TextField source="weekDay" />
        <TextField source="exportHourUtc" />
        <TextField source="exportMode" />
        <BooleanField source="isActive" label="Active?" />
        <TextField source="nextExportAt" />
        <TextField source="lastExportAt" />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
